// endpoints.ts
/**
 * This file contains the endpoints for the API.
 */
// ------------------------------------------------------------
import type {
  NewItemSchemas,
  UpdateFolderResultCellRequest,
  UpdateItemSchemas,
  UpdateParentChildItemSchema,
  UpdateTaskStatusSchema,
} from '@/schemas/folder';
import type {
  AddNewItemResponse,
  CommentDetails,
  CreateOrganizationRequest,
  FolderDetails,
  FolderResultRow,
  FolderResultsResponse,
  ListItemAssignmentsOrEditors,
  LoginRequest,
  LoginResponse,
  MediaResourceUploadResponse,
  Organization,
  RefreshTokenRequest,
  RefreshTokenResponse,
  UserDetails,
} from '@/types';
import type { CreateNewAIConversationRequest, NewAIConversation, SendMessageToAIConversationRequest, SendMessageToAIConversationResponse, ConversationDetails, ConversationSummary } from '@/types/ai-chat';
import type { GroupDetails, GroupUserRole } from '@/types/groups';
import type {
  CreateWorkflowDescriptionRequest,
  CreateWorkflowInstanceRequestRequest,
  DeleteGroupResponse,
  PublishWorkflowDescriptionRequest,
  UpdateWorkflowDescriptionRequest,
  WorkflowDescription,
  WorkflowInstanceCreateRequest,
} from '@/types/workflow';
import type { UserTaskListItem } from '@/types';
import type { UserNotificationDetails } from '@/types/notifications';
import type {
  AddGroupChatMessageRequest,
  GroupChatMessageDetails,
  GroupChatMessagesResponse,
  GroupChatUnreadCountDetails,
  UpdateGroupChatReadHistoryRequest,
  UpdateGroupChatReadHistoryResponse,
} from '@/types/comments';
import type { z } from 'zod';
import { UserTasksParams } from './services/tasks';
// ------------------------------------------------------------

// ------------------------------------------------------------
// #region Endpoint Config
/**
 * Type for defining an endpoint with its request and response types
 */
export type EndpointConfig<TRequest = void, TResponse = void> = {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  path: string | ((...args: Array<string | number | boolean | null | undefined>) => string);
  requestType?: TRequest;
  responseType?: TResponse;
};

const BASE_PATHS = {
  AUTH: 'api/v1/auth',
  FOLDER: 'api/v1/list-item',
  /** List-item details (GET by id / list all) — v2 API */
  FOLDER_DETAILS: 'api/v2/list-item',
  /** A folder's results table (list, cell edit) */
  FOLDER_RESULTS: 'api/v1/folders',
  ORGANIZATION: 'api/v1/organizations',
  GROUP_CHAT: 'api/v1/group-chat',
  GROUP_CHAT_READ_HISTORY: 'api/v1/group-chat-read-history',
  USER: 'api/v1/users',
  GROUP_ASSIGNMENT: 'api/v1/group-assignment',
  UPLOAD_MEDIA_RESOURCE: 'api/v1/media-resource',
  UPLOAD_ATTACHMENT_RESOURCE: 'api/v1/attachment-resource',
  GROUPS: 'api/v1/groups',
  AI_TASK: 'api/v1/ai-task',
  NOTIFICATIONS: 'api/v1/notification',
  ITEM_READ_HISTORY: 'api/v1/item-read-history',
  AI_CHAT: 'api/v1/ai-chat/conversations',
  WORKFLOW: 'api/v1/workflow',
};

export const ENDPOINTS = {
  /**
   * Authentication endpoints
   */
  AUTH: {
    BASE: BASE_PATHS.AUTH,
    /**
     * Login endpoint.
     * @description Login user with email and password.
     * @example ```ts
     * const response: ENDPOINTS.AUTH.LOGIN.responseType = await api.post(ENDPOINTS.AUTH.LOGIN, { username, password });
     * ```
     */
    LOGIN: {
      method: "POST",
      path: "/api/v1/auth/login",
      requestType: {} as LoginRequest,
      responseType: {} as LoginResponse,
    } satisfies EndpointConfig<LoginRequest, LoginResponse>,
    REFRESH_TOKEN: {
      method: "POST",
      path: `${BASE_PATHS.AUTH}/refresh-token`,
      requestType: {} as RefreshTokenRequest,
      responseType: {} as RefreshTokenResponse,
    } satisfies EndpointConfig<RefreshTokenRequest, RefreshTokenResponse>,
    LOGOUT: {
      method: "POST",
      path: `${BASE_PATHS.AUTH}/logout`,
      requestType: {} as unknown as void,
      responseType: {} as unknown as void,
    } satisfies EndpointConfig<void, void>,
  },
  GROUP_CHAT: {
    BASE: BASE_PATHS.GROUP_CHAT,
    GET_MESSAGES: {
      method: "GET",
      path: (groupId, status = "all", pageNumber = 1, pageSize = 500) =>
        `${BASE_PATHS.GROUP_CHAT}/all/${String(groupId)}?status=${String(status)}&pageNumber=${String(pageNumber)}&pageSize=${String(pageSize)}`,
      requestType: {} as unknown as void,
      responseType: {} as GroupChatMessagesResponse,
    } satisfies EndpointConfig<void, GroupChatMessagesResponse>,
    ADD_MESSAGE: {
      method: "POST",
      path: (groupId) => `${BASE_PATHS.GROUP_CHAT}/add/${String(groupId)}`,
      requestType: {} as AddGroupChatMessageRequest,
      responseType: {} as GroupChatMessageDetails,
    } satisfies EndpointConfig<AddGroupChatMessageRequest, GroupChatMessageDetails>,
    DELETE_MESSAGE: {
      method: "DELETE",
      path: (messageId, groupId) =>
        `${BASE_PATHS.GROUP_CHAT}/delete/${String(messageId)}?groupId=${String(groupId)}`,
      requestType: {} as unknown as void,
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      void,
      { code: number | string; message: string }
    >,
    UPDATE_READ_HISTORY: {
      method: "PUT",
      path: `${BASE_PATHS.GROUP_CHAT_READ_HISTORY}/update`,
      requestType: {} as UpdateGroupChatReadHistoryRequest,
      responseType: {} as UpdateGroupChatReadHistoryResponse,
    } satisfies EndpointConfig<
      UpdateGroupChatReadHistoryRequest,
      UpdateGroupChatReadHistoryResponse
    >,
    UNREAD_MESSAGES_COUNT: {
      method: "GET",
      path: (groupId) =>
        `${BASE_PATHS.GROUP_CHAT_READ_HISTORY}/unread-messages-count/${String(groupId)}`,
      requestType: {} as unknown as void,
      responseType: {} as GroupChatUnreadCountDetails,
    } satisfies EndpointConfig<void, GroupChatUnreadCountDetails>,
  },
  FOLDER: {
    BASE: BASE_PATHS.FOLDER,
    /**
     * List all tasks for current user (manager/worker)
     * Optional filters: status (OPEN|DONE), groupId
     * GET /api/v1/list-item/tasks-list?status=OPEN&groupId=123
     */
    GET_USER_TASKS_LIST: {
      method: "GET",
      path: (status?: string | number, groupId?: string | number) => {
        const q: string[] = [];
        if (status) q.push(`status=${encodeURIComponent(String(status))}`);
        if (groupId) q.push(`groupId=${encodeURIComponent(String(groupId))}`);
        const qs = q.length ? `?${q.join("&")}` : "";
        return `${BASE_PATHS.FOLDER}/tasks-list${qs}`;
      },
      requestType: {} as {
        status?: UserTasksParams["status"];
        groupId?: string | number;
      },
      responseType: {} as Array<UserTaskListItem>,
    } satisfies EndpointConfig<
      { status?: UserTasksParams["status"]; groupId?: string | number },
      Array<UserTaskListItem>
    >,
    GET_FOLDER_BY_ID: {
      method: "GET",
      path: (id, groupId) =>
        `${BASE_PATHS.FOLDER_DETAILS}/details?id=${String(id)}&groupId=${String(groupId)}`,
      requestType: {} as unknown as void,
      responseType: {} as FolderDetails,
    } satisfies EndpointConfig<void, FolderDetails>,
    GET_ALL_FOLDERS: {
      method: "GET",
      path: `${BASE_PATHS.FOLDER_DETAILS}/details`,
      requestType: {} as unknown as void,
      responseType: {} as FolderDetails,
    } satisfies EndpointConfig<void, FolderDetails>,
    ADD_NEW_ITEM_IN_FOLDER: {
      method: "POST",
      path: `${BASE_PATHS.FOLDER}/add`,
      requestType: {} as z.infer<
        (typeof NewItemSchemas)[keyof typeof NewItemSchemas]
      >,
      responseType: {} as AddNewItemResponse,
    } satisfies EndpointConfig<
      z.infer<(typeof NewItemSchemas)[keyof typeof NewItemSchemas]>,
      AddNewItemResponse
    >,
    UPDATE_CHILD_ITEM: {
      method: "PUT",
      path: `${BASE_PATHS.FOLDER}/update-child-item`,
      requestType: {} as z.infer<
        (typeof UpdateItemSchemas)[keyof typeof UpdateItemSchemas]
      >,
      responseType: {} as FolderDetails,
    } satisfies EndpointConfig<
      z.infer<(typeof UpdateItemSchemas)[keyof typeof UpdateItemSchemas]>,
      FolderDetails
    >,
    UPDATE_PARENT_CHILD_ITEM: {
      method: "PUT",
      path: `${BASE_PATHS.FOLDER}/update-parent-child-item`,
      requestType: {} as z.infer<typeof UpdateParentChildItemSchema>,
      responseType: {} as FolderDetails,
    } satisfies EndpointConfig<
      z.infer<typeof UpdateParentChildItemSchema>,
      FolderDetails
    >,
    UPDATE_TASK_STATUS: {
      method: "PUT",
      path: (taskId) =>
        `${BASE_PATHS.FOLDER}/update-task-status/${String(taskId)}`,
      requestType: {} as z.infer<typeof UpdateTaskStatusSchema>,
      responseType: {} as FolderDetails,
    } satisfies EndpointConfig<
      z.infer<typeof UpdateTaskStatusSchema>,
      FolderDetails
    >,
    DELETE_LIST_ITEM: {
      method: "DELETE",
      path: `${BASE_PATHS.FOLDER}/delete-list-items`,
      requestType: {} as { itemListIds: Array<number>; groupId: string },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { itemListIds: Array<number>; groupId: string },
      { code: number | string; message: string }
    >,
    MARK_LIST_ITEM_AS_READ: {
      method: "PUT",
      path: `${BASE_PATHS.ITEM_READ_HISTORY}/mark-read`,
      requestType: {} as { groupId: number; listItemId: number },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { groupId: number; listItemId: number },
      { code: number | string; message: string }
    >,
    /**
     * Move items to a target folder
     * @description Move multiple items from their current folder to a target folder (supports cross-group when targetGroupId provided)
     * @example ```ts
     * const response = await api.put(ENDPOINTS.FOLDER.MOVE_ITEMS, {
     *   itemListIds: [1, 2, 3],
     *   newParentId: 5,
     *   groupId: 'source-group-id',
     *   targetGroupId: 'target-group-id' // optional; if omitted, moves within same group
     * });
     * ```
     */
    MOVE_ITEMS: {
      method: "PUT",
      path: `${BASE_PATHS.FOLDER}/move-items`,
      requestType: {} as {
        itemListIds: Array<number>;
        newParentId: number;
        groupId: string;
        targetGroupId?: string; // when moving across groups
      },
      responseType: {} as FolderDetails,
    } satisfies EndpointConfig<
      {
        itemListIds: Array<number>;
        newParentId: number;
        groupId: string;
        targetGroupId?: string;
      },
      FolderDetails
    >,
    /**
     * Duplicate a list item
     * @description Copy a list item into same or different folder/group
     */
    DUPLICATE_LIST_ITEM: {
      method: "POST",
      path: `${BASE_PATHS.FOLDER}/duplicate-list-item`,
      requestType: {} as {
        listItemId: number;
        groupId: string; // source group id
        targetParentId: number; // -1 for home
        targetGroupId: string; // target group id
      },
      responseType: {} as AddNewItemResponse,
    } satisfies EndpointConfig<
      {
        listItemId: number;
        groupId: string;
        targetParentId: number;
        targetGroupId: string;
      },
      AddNewItemResponse
    >,
    /**
     * Transcode File
     * POST /api/v1/list-item/transcode
     */
    TRANSCODE_FILE: {
      method: "POST",
      path: `${BASE_PATHS.FOLDER}/transcode`,
      requestType: {} as {
        listItemId: string | number;
        groupId: number | string;
        resourceId: number;
      },
      responseType: {} as import("@/types/transcode").TranscodeJob,
    } satisfies EndpointConfig<
      {
        listItemId: string | number;
        groupId: number | string;
        resourceId: number;
      },
      import("@/types/transcode").TranscodeJob
    >,
    /**
     * Transcode Job Details by Job ID
     * GET /api/v1/list-item/transcode/job-status/{transcodeJobId}?groupId={groupId}
     */
    TRANSCODE_JOB_STATUS_BY_ID: {
      method: "GET",
      path: (transcodeJobId: string | number, groupId: string | number) =>
        `${BASE_PATHS.FOLDER}/transcode/job-status/${String(transcodeJobId)}?groupId=${String(groupId)}`,
      requestType: {} as unknown as void,
      responseType: {} as import("@/types/transcode").TranscodeJobWithDetails,
    } satisfies EndpointConfig<
      void,
      import("@/types/transcode").TranscodeJobWithDetails
    >,
    /**
     * Transcode Jobs for Parent
     * GET /api/v1/list-item/transcode/job-status/?parentId={itemId}&groupId={groupId}
     */
    TRANSCODE_JOBS_BY_PARENT: {
      method: "GET",
      path: (parentId: string | number, groupId: string | number) =>
        `${BASE_PATHS.FOLDER}/transcode/job-status/?parentId=${String(parentId)}&groupId=${String(groupId)}`,
      requestType: {} as unknown as void,
      responseType: {} as Array<import("@/types/transcode").TranscodeJob>,
    } satisfies EndpointConfig<
      void,
      Array<import("@/types/transcode").TranscodeJob>
    >,
    /**
     * Update Transcoded Page Text or Review Status
     * PUT /api/v1/list-item/transcode/page-text
     * Body: {
     *   listItemId: string|number,
     *   groupId: number|string,
     *   text?: string,
     *   pageReviewCompleted?: boolean,
     *   retryPrompt?: string
     * }
     */
    TRANSCODE_UPDATE_PAGE_TEXT: {
      method: "PUT",
      path: `${BASE_PATHS.FOLDER}/transcode/page-text`,
      requestType: {} as {
        listItemId: string | number;
        groupId: number | string;
        text?: string;
        pageReviewCompleted?: boolean;
        retryPrompt?: string;
      },
      responseType: {} as
        | { code?: number | string; message?: string }
        | unknown,
    } satisfies EndpointConfig<
      {
        listItemId: string | number;
        groupId: number | string;
        text?: string;
        pageReviewCompleted?: boolean;
        retryPrompt?: string;
      },
      { code?: number | string; message?: string } | unknown
    >,
    /**
     * Update the QC result of a transcoded page whose `resultFormat` is JSON.
     * PUT /api/v1/list-item/task-qc-result/json
     * The backend rejects these pages on `transcode/page-text` ("QC JSON page body must be updated via ...").
     * Body: {
     *   listItemId: string|number,   // the transcoded TEXT page item (same id page-text takes)
     *   parentId: string|number,     // the TASK the page belongs to (the folder being reviewed)
     *   epicId: string|number,       // the TASK's parent EPIC — the backend resolves the Temporal workflow that validates the JSON from it
     *   groupId: number|string,
     *   taskTableVersion: number,    // the table's current version: the one the UI last loaded, or the one the previous save returned; any other is rejected with 1244 (QC_TASK_TABLE_VERSION_CONFLICT)
     *   resultGranularity?: string,  // `DOCUMENT` for a whole-document result
     *   text: string                 // the page's full task-grid payload as a JSON string; must be valid JSON
     * }
     * Success returns the updated TEXT list-item (`ListItemDto`), the canonical JSON in `aiTranscodeParagraphDto.qcPayload`,
     * and the table's new `taskTableVersion`.
     * Invalid JSON or a failed workflow validation is a validation error and nothing is saved.
     */
    TASK_QC_RESULT_JSON_UPDATE: {
      method: "PUT",
      path: `${BASE_PATHS.FOLDER}/task-qc-result/json`,
      requestType: {} as {
        listItemId: string | number;
        parentId: string | number;
        epicId: string | number;
        groupId: number | string;
        taskTableVersion: number;
        resultGranularity?: string;
        text: string;
      },
      responseType: {} as unknown,
    } satisfies EndpointConfig<
      {
        listItemId: string | number;
        parentId: string | number;
        epicId: string | number;
        groupId: number | string;
        taskTableVersion: number;
        resultGranularity?: string;
        text: string;
      },
      unknown
    >,
    /**
     * Save one cell of a QC task table (the grid's blur save).
     * PUT /api/v1/list-item/task-qc-result/json/cell
     * Body: {
     *   taskId: string|number,           // the TASK the table belongs to (the folder being reviewed)
     *   groupId: number|string,
     *   epicId: string|number,
     *   listItemId: string|number,       // the TEXT result item holding the table
     *   taskTableVersion: number,        // the table's current version, see TASK_QC_RESULT_JSON_UPDATE; any other is rejected with 1244
     *   path: string,                    // e.g. `data[3].chem_mn.value`
     *   value: string|number|boolean|null,
     *   recordId?: string, recordVersion?: string   // when the table row is backed by a stored record
     * }
     * Success returns the updated table, with validation feedback (`color` / `message`) merged into its cells,
     * and the new `taskTableVersion`.
     */
    TASK_QC_RESULT_JSON_CELL_UPDATE: {
      method: "PUT",
      path: `${BASE_PATHS.FOLDER}/task-qc-result/json/cell`,
      requestType: {} as {
        taskId: string | number;
        groupId: number | string;
        epicId: string | number;
        listItemId: string | number;
        taskTableVersion: number;
        path: string;
        value: string | number | boolean | null;
        recordId?: string;
        recordVersion?: string;
      },
      responseType: {} as unknown,
    } satisfies EndpointConfig<
      {
        taskId: string | number;
        groupId: number | string;
        epicId: string | number;
        listItemId: string | number;
        taskTableVersion: number;
        path: string;
        value: string | number | boolean | null;
        recordId?: string;
        recordVersion?: string;
      },
      unknown
    >,
  },
  ORGANIZATION: {
    BASE: "v1/organizations",
    CREATE_ORGANIZATION: {
      method: "POST",
      path: "v1/organizations/create",
      requestType: {} as CreateOrganizationRequest,
      responseType: {} as Organization,
    } satisfies EndpointConfig<CreateOrganizationRequest, Organization>,
  },
  USER: {
    BASE: BASE_PATHS.USER,
    GET_ALL_USERS: {
      method: "GET",
      path: `${BASE_PATHS.USER}/all/group`,
      requestType: {} as unknown as void,
      responseType: {} as Array<UserDetails>,
    } satisfies EndpointConfig<void, Array<UserDetails>>,
    GET_CURRENT_USER_DETAILS: {
      method: "GET",
      path: `${BASE_PATHS.USER}`,
      requestType: {} as unknown as void,
      responseType: {} as UserDetails,
    } satisfies EndpointConfig<void, UserDetails>,
    /**
     * Search user(s) by email
     */
    SEARCH_USER_BY_EMAIL: {
      method: "GET",
      path: (...args) =>
        `${BASE_PATHS.USER}/search?email=${encodeURIComponent(String(args[0] ?? ""))}`,
      requestType: {} as unknown as void,
      responseType: {} as Array<UserDetails>,
    } satisfies EndpointConfig<void, Array<UserDetails>>,
    /**
     * Invite a user by email (optionally to a specific group)
     */
    INVITE_USER: {
      method: "POST",
      path: `${BASE_PATHS.USER}/invite`,
      requestType: {} as { email: string; groupId?: number },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { email: string; groupId?: number },
      { code: number | string; message: string }
    >,
  },
  GROUP_ASSIGNMENT: {
    BASE: BASE_PATHS.GROUP_ASSIGNMENT,
    GET_ALL_ASSIGNMENTS: {
      method: "GET",
      path: (groupId) =>
        `${BASE_PATHS.GROUP_ASSIGNMENT}/all-assignments/${String(groupId)}`,
      requestType: {} as unknown as void,
      responseType: {} as Array<ListItemAssignmentsOrEditors>,
    } satisfies EndpointConfig<void, Array<ListItemAssignmentsOrEditors>>,
    ADD_SUBSCRIBER: {
      method: "POST",
      path: `${BASE_PATHS.GROUP_ASSIGNMENT}/subscriber/add`,
      requestType: {} as { userId: string; groupId: number },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userId: string; groupId: number },
      { code: number | string; message: string }
    >,
    REMOVE_SUBSCRIBER: {
      method: "DELETE",
      path: `${BASE_PATHS.GROUP_ASSIGNMENT}/subscriber/remove`,
      requestType: {} as { userId: string; groupId: number },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userId: string; groupId: number },
      { code: number | string; message: string }
    >,
    ADD_EDITOR: {
      method: "POST",
      path: `${BASE_PATHS.GROUP_ASSIGNMENT}/add-editor`,
      requestType: {} as { userId: string; groupId: number },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userId: string; groupId: number },
      { code: number | string; message: string }
    >,
    REMOVE_EDITOR: {
      method: "DELETE",
      path: `${BASE_PATHS.GROUP_ASSIGNMENT}/remove-editor`,
      requestType: {} as { userId: string; groupId: number },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userId: string; groupId: number },
      { code: number | string; message: string }
    >,
  },
  UPLOAD: {
    BASE: "",
    UPLOAD_FILE: {
      method: "POST",
      path: (fileType) =>
        `${fileType === "media" || fileType === "MEDIA" ? BASE_PATHS.UPLOAD_MEDIA_RESOURCE : BASE_PATHS.UPLOAD_ATTACHMENT_RESOURCE}/add`,
      requestType: {} as { formData: FormData },
      responseType: {} as MediaResourceUploadResponse,
    } satisfies EndpointConfig<
      { formData: FormData },
      MediaResourceUploadResponse
    >,
  },
  GROUPS: {
    BASE: BASE_PATHS.GROUPS,
    GET_ALL_GROUPS: {
      method: "GET",
      path: `${BASE_PATHS.GROUPS}`,
      requestType: {} as unknown as void,
      responseType: {} as Array<GroupDetails>,
    } satisfies EndpointConfig<void, Array<GroupDetails>>,
    GET_GROUP_DETAILS_BY_ID: {
      method: "GET",
      path: (id) => `${BASE_PATHS.GROUPS}/${String(id)}`,
      requestType: {} as unknown as void,
      responseType: {} as GroupDetails,
    } satisfies EndpointConfig<void, GroupDetails>,
    GET_USERS_BY_GROUP: {
      method: "GET",
      path: `${BASE_PATHS.USER}/all/group`,
      requestType: {} as unknown as void,
      responseType: {} as Array<
        UserDetails & {
          groups: Array<{ id: number; name: string; isPrivate: boolean }>;
        }
      >,
    } satisfies EndpointConfig<
      void,
      Array<
        UserDetails & {
          groups: Array<{ id: number; name: string; isPrivate: boolean }>;
        }
      >
    >,
    CREATE_NEW_GROUP: {
      method: "POST",
      path: `${BASE_PATHS.GROUPS}/create`,
      requestType: {} as { name: string },
      responseType: {} as GroupDetails,
    } satisfies EndpointConfig<{ name: string }, GroupDetails>,
    ADD_MEMBERS_TO_GROUP: {
      method: "PUT",
      path: (groupId) => `${BASE_PATHS.GROUPS}/add-users/${String(groupId)}`,
      requestType: {} as { userIds: Array<number> },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userIds: Array<number> },
      { code: number | string; message: string }
    >,
    ADD_ADMINS_TO_GROUP: {
      method: "PUT",
      path: (groupId) => `${BASE_PATHS.GROUPS}/add-admin/${String(groupId)}`,
      requestType: {} as { userIds: Array<number> },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userIds: Array<number> },
      { code: number | string; message: string }
    >,
    REMOVE_MEMBERS_FROM_GROUP: {
      method: "DELETE",
      path: (groupId) => `${BASE_PATHS.GROUPS}/remove-users/${String(groupId)}`,
      requestType: {} as { userIds: Array<number> },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userIds: Array<number> },
      { code: number | string; message: string }
    >,
    UPDATE_GROUP_NAME: {
      method: "PUT",
      path: (groupId) => `${BASE_PATHS.GROUPS}/update/${String(groupId)}`,
      requestType: {} as { name: string },
      responseType: {} as GroupDetails,
    } satisfies EndpointConfig<{ name: string }, GroupDetails>,
    SORT_GROUPS: {
      method: "PUT",
      path: `${BASE_PATHS.GROUPS}/sort-groups`,
      requestType: {} as {
        sortGroupData: Array<{
          groupId: string;
          index: string;
        }>;
      },
      responseType: {} as Array<GroupDetails>,
    } satisfies EndpointConfig<
      {
        sortGroupData: Array<{
          groupId: string;
          index: string;
        }>;
      },
      Array<GroupDetails>
    >,
    REMOVE_ADMINS_FROM_GROUP: {
      method: "PUT",
      path: (groupId) => `${BASE_PATHS.GROUPS}/remove-admin/${String(groupId)}`,
      requestType: {} as { userIds: Array<number> },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userIds: Array<number> },
      { code: number | string; message: string }
    >,
    UPDATE_GROUP_ROLE: {
      method: "PUT",
      path: (groupId) => `${BASE_PATHS.GROUPS}/update-role/${String(groupId)}`,
      requestType: {} as { userId: string; role: GroupUserRole },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { userId: string; role: GroupUserRole },
      { code: number | string; message: string }
    >,
    DELETE_GROUP: {
      method: "DELETE",
      path: (groupId) => `${BASE_PATHS.GROUPS}/delete/${String(groupId)}`,
      requestType: {} as unknown as void,
      responseType: {} as DeleteGroupResponse,
    } satisfies EndpointConfig<void, DeleteGroupResponse>,
  },
  NOTIFICATIONS: {
    BASE: BASE_PATHS.NOTIFICATIONS,
    GET_ALL_USER_NOTIFICATIONS: {
      method: "GET",
      path: `${BASE_PATHS.NOTIFICATIONS}/user`,
      requestType: {
        params: {
          pageNo: 1,
          pageSize: 10,
        },
      } as unknown as {
        params: {
          pageNo: number;
          pageSize: number;
        };
      },
      responseType: {} as UserNotificationDetails,
    } satisfies EndpointConfig<
      {
        params: {
          pageNo: number;
          pageSize: number;
        };
      },
      UserNotificationDetails
    >,
    MARK_NOTIFICATIONS_READ: {
      method: "PUT",
      path: `${BASE_PATHS.NOTIFICATIONS}/user/mark-read`,
      requestType: {} as {
        notificationIds: Array<string | number>;
        groupId: number;
      },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      {
        notificationIds: Array<string | number>;
        groupId: number;
      },
      { code: number | string; message: string }
    >,
  },
  AI_TASK: {
    EXECUTE_AI_TASK: {
      method: "POST",
      path: (aiTaskListItemId, groupId) =>
        `api/v1/ai-task/${String(aiTaskListItemId)}/execute?groupId=${String(groupId)}`,
      requestType: {} as { aiTaskListItemId: number; groupId: string },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { aiTaskListItemId: number; groupId: string },
      { code: number | string; message: string }
    >,
    GET_AI_TASK_RESULT: {
      method: "GET",
      path: (aiTaskListItemId, groupId) =>
        `api/v1/ai-task/${String(aiTaskListItemId)}/results?groupId=${String(groupId)}`,
      requestType: {} as { aiTaskListItemId: number; groupId: string },
      responseType: {} as {
        aiTaskId: number;
        listItemId: number;
        title: string;
        results: {
          id: number;
          prompt: string;
          answer: string;
          executedBy: string;
          status: string;
          processCompletedAt: string;
          processStartedAt: string;
        }[];
      },
    } satisfies EndpointConfig<
      { aiTaskListItemId: number; groupId: string },
      {
        aiTaskId: number;
        listItemId: number;
        title: string;
        results: {
          id: number;
          prompt: string;
          answer: string;
          executedBy: string;
          status: string;
          processCompletedAt: string;
          processStartedAt: string;
        }[];
      }
    >,
    LINK_LIST_ITEMS: {
      method: "PUT",
      path: (listItemId) =>
        `${BASE_PATHS.AI_TASK}/${String(listItemId)}/link-list-items`,
      requestType: {} as {
        groupId: number | string;
        itemListIds: Array<number>;
      },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { groupId: number | string; itemListIds: Array<number> },
      { code: number | string; message: string }
    >,
    DELINK_LIST_ITEMS: {
      method: "DELETE",
      path: (listItemId) =>
        `${BASE_PATHS.AI_TASK}/${String(listItemId)}/link-list-items`,
      requestType: {} as {
        groupId: number | string;
        itemListIds: Array<number>;
      },
      responseType: {} as { code: number | string; message: string },
    } satisfies EndpointConfig<
      { groupId: number | string; itemListIds: Array<number> },
      { code: number | string; message: string }
    >,
  } as const,
  AI_CHAT: {
    BASE: BASE_PATHS.AI_CHAT,
    CREATE_NEW_AI_CONVERSATION: {
      method: "POST",
      path: `${BASE_PATHS.AI_CHAT}/create`,
      requestType: {} as CreateNewAIConversationRequest,
      responseType: {} as NewAIConversation,
    } satisfies EndpointConfig<
      CreateNewAIConversationRequest,
      NewAIConversation
    >,
    SEND_MESSAGE_TO_AI_CONVERSATION: {
      method: "POST",
      path: `${BASE_PATHS.AI_CHAT}/messages/send`,
      requestType: {} as SendMessageToAIConversationRequest,
      responseType: {} as SendMessageToAIConversationResponse,
    } satisfies EndpointConfig<
      SendMessageToAIConversationRequest,
      SendMessageToAIConversationResponse
    >,
    GET_CONVERSATION_DETAILS: {
      method: "GET",
      path: (conversationId, groupId) =>
        `${BASE_PATHS.AI_CHAT}/${String(conversationId)}?groupId=${String(groupId)}`,
      requestType: {} as { conversationId: string; groupId: string },
      responseType: {} as ConversationDetails,
    } satisfies EndpointConfig<
      { conversationId: string; groupId: string },
      ConversationDetails
    >,
    GET_ALL_CONVERSATIONS: {
      method: "GET",
      path: (groupId) => `${BASE_PATHS.AI_CHAT}/all?groupId=${String(groupId)}`,
      requestType: {} as { groupId: string },
      responseType: {} as ConversationSummary[],
    } satisfies EndpointConfig<{ groupId: string }, ConversationSummary[]>,
  } as const,
  /**
   * A folder's results table: stored records, shaped by a schema that says which columns can be sorted, filtered
   * and edited.
   */
  FOLDER_RESULTS: {
    BASE: BASE_PATHS.FOLDER_RESULTS,
    /**
     * List the folder's result rows.
     * GET /api/v1/folders/{folderId}/results?groupId=&limit=&offset=&sortBy=&sortDir=&<filterable column key>=
     * `queryString` is built by `buildFolderResultsQueryString` and is part of the path (see there for why).
     */
    LIST: {
      method: "GET",
      path: (folderId, queryString) =>
        `${BASE_PATHS.FOLDER_RESULTS}/${String(folderId)}/results?${String(queryString ?? '')}`,
      requestType: {} as unknown as void,
      responseType: {} as FolderResultsResponse,
    } satisfies EndpointConfig<void, FolderResultsResponse>,
    /**
     * Edit one cell of a result row.
     * PATCH /api/v1/folders/{folderId}/results/{recordId}/cell
     * Body: { groupId, expectedRecordVersion, columnKey, newValue }
     * Success returns the updated row (`recordVersion` bumped, `resultData` refreshed).
     * Errors: 1240 stale `expectedRecordVersion`, 1247 column not editable, 1250 value rejected (nothing saved).
     */
    UPDATE_CELL: {
      method: "PATCH",
      path: (folderId, recordId) =>
        `${BASE_PATHS.FOLDER_RESULTS}/${String(folderId)}/results/${String(recordId)}/cell`,
      requestType: {} as UpdateFolderResultCellRequest,
      responseType: {} as FolderResultRow,
    } satisfies EndpointConfig<UpdateFolderResultCellRequest, FolderResultRow>,
  },
  WORKFLOW: {
    BASE: BASE_PATHS.WORKFLOW,
    LIST_DESCRIPTIONS: {
      method: "GET",
      path: (groupId, isPublished?: boolean | null) => {
        const params = new URLSearchParams({ groupId: String(groupId) });
        if (isPublished !== undefined && isPublished !== null) {
          params.set('isPublished', String(isPublished));
        }
        return `${BASE_PATHS.WORKFLOW}/descriptions?${params.toString()}`;
      },
      requestType: {} as { groupId: number | string; isPublished?: boolean | null },
      responseType: {} as Array<WorkflowDescription>,
    } satisfies EndpointConfig<
      { groupId: number | string; isPublished?: boolean | null },
      Array<WorkflowDescription>
    >,
    GET_DESCRIPTION_BY_ID: {
      method: "GET",
      path: (id, groupId) =>
        `${BASE_PATHS.WORKFLOW}/descriptions/${String(id)}?groupId=${String(groupId)}`,
      requestType: {} as { id: number | string; groupId: number | string },
      responseType: {} as WorkflowDescription,
    } satisfies EndpointConfig<
      { id: number | string; groupId: number | string },
      WorkflowDescription
    >,
    CREATE_DESCRIPTION: {
      method: "POST",
      path: `${BASE_PATHS.WORKFLOW}/descriptions`,
      requestType: {} as CreateWorkflowDescriptionRequest,
      responseType: {} as WorkflowDescription,
    } satisfies EndpointConfig<CreateWorkflowDescriptionRequest, WorkflowDescription>,
    UPDATE_DESCRIPTION: {
      method: "PUT",
      path: (id) => `${BASE_PATHS.WORKFLOW}/descriptions/${String(id)}`,
      requestType: {} as UpdateWorkflowDescriptionRequest,
      responseType: {} as WorkflowDescription,
    } satisfies EndpointConfig<UpdateWorkflowDescriptionRequest, WorkflowDescription>,
    PUBLISH_DESCRIPTION: {
      method: "PUT",
      path: (id, groupId) =>
        `${BASE_PATHS.WORKFLOW}/descriptions/${String(id)}/publish?groupId=${String(groupId)}`,
      requestType: {} as PublishWorkflowDescriptionRequest,
      responseType: {} as WorkflowDescription,
    } satisfies EndpointConfig<PublishWorkflowDescriptionRequest, WorkflowDescription>,
    CREATE_INSTANCE_REQUEST: {
      method: "POST",
      path: `${BASE_PATHS.WORKFLOW}/instance-create-requests`,
      requestType: {} as CreateWorkflowInstanceRequestRequest,
      responseType: {} as WorkflowInstanceCreateRequest,
    } satisfies EndpointConfig<
      CreateWorkflowInstanceRequestRequest,
      WorkflowInstanceCreateRequest
    >,
  } as const,
};

// #endregion
